import {
  array,
  assert,
  func,
  literal,
  number,
  object,
  optional,
  string,
  define,
  union,
  type,
  boolean,
  coerce,
  create,
} from "superstruct";

type Schema = Record<string, any>;

// Generic helper that coerces then validates and returns the normalized payload.
// It preserves the original error style for your tests.
function assertCustom(typeName: string, schema: Schema, payload: any) {
  try {
    // create() applies coercion (if any) before validating
    return create(payload, object(schema));
  } catch (error: any) {
    throw new Error(
      `assertion error: invalid ${typeName} payload: ${error.message}`,
    );
  }
}

const component = union([
  array(
    object({
      name: string(),
      position: number(),
    }),
  ),
  define("Element", (value) => value instanceof Element),
]);

const searchLevel = union([literal("primary"), literal("secondary")]);

const language = union([
  literal("de"),
  literal("fr"),
  literal("it"),
  literal("en"),
]);

// Coercion for loginStatus:
// - Accept inputs: 0, 1, "0", "1", null
// - Normalize to: "0" | "1" | undefined (undefined only when input is null/undefined)
// Note: We coerce an optional struct so that null -> undefined happens before optional handling.
const LoginStatusNormalized = coerce(
  optional(union([literal("0"), literal("1")])),
  union([literal(0), literal(1), literal("0"), literal("1"), literal(null)]),
  (v) => (v == null ? undefined : v === 1 || v === "1" ? "1" : "0"),
);

const userSchema = object({
  loginType: optional(string()),
  // Use coercing loginStatus so numbers are accepted and normalized
  loginStatus: LoginStatusNormalized,
  language: optional(language),
  UUID: optional(string()),
  age: optional(string()),
  gender: optional(union([literal("f"), literal("m"), literal("n")])),
  zipcode: optional(string()),
});

const searchSchema = {
  value: string(),
  results: number(),
  level: searchLevel,
};

const pageLoaded = (payload: any) =>
  assertCustom(
    "pageLoaded",
    {
      pageInstanceID: string(),
      pageID: optional(string()),
      pageName: string(),
      destinationURI: string(),
      sysEnv: optional(string()),
      language,
      primaryCategory: string(),
      errorCode: optional(string()),
      errorMessage: optional(string()),
      user: optional(userSchema),
      search: optional(object(searchSchema)),
      referrer: optional(string()),
    },
    payload,
  );

const searchLoaded = (payload: any) =>
  assertCustom("searchLoaded", searchSchema, payload);

const genericSchema = {
  component,
  label: string(),
  location: optional(string()),
  variant: optional(string()),
  detail: optional(string()),
};

const accordion = (payload?: any) =>
  assertCustom(
    "accordion",
    {
      ...genericSchema,
      action: union([literal("open"), literal("close")]),
      headline: optional(string()),
    },
    payload,
  );

const genericNavigationSchema = {
  ...genericSchema,
  destination: string(),
  callback: optional(func()),
};

const genericNavigation = (payload: any) =>
  assertCustom("genericNavigation", genericNavigationSchema, payload);

const customNavigation = (payload: any) =>
  assertCustom(
    "customNavigation",
    {
      ...genericNavigationSchema,
      action: optional(string()),
      primaryCategory: optional(string()),
    },
    payload,
  );

const genericClick = (payload: any) =>
  assertCustom("genericClick", genericSchema, payload);

const teaserImpression = (payload: any) =>
  assertCustom(
    "teaserImpression",
    {
      ...genericSchema,
      destination: optional(string()),
      headline: string(),
      topic: optional(string()),
      mustRead: optional(boolean()),
      isAccessibleExternally: optional(boolean()),
      targetGroup: optional(array(string())),
      theme: optional(array(string())),
      division: optional(array(string())),
      postType: optional(array(string())),
      contentRegion: optional(array(string())),
    },
    payload,
  );

const teaserNavigation = (payload: any) =>
  assertCustom(
    "teaserNavigation",
    {
      ...genericNavigationSchema,
      headline: string(),
      location: optional(string()),
      variant: optional(string()),
      detail: optional(string()),
      topic: optional(string()),
      mustRead: optional(boolean()),
      isAccessibleExternally: optional(boolean()),
      targetGroup: optional(array(string())),
      theme: optional(array(string())),
      division: optional(array(string())),
      postType: optional(array(string())),
      contentRegion: optional(array(string())),
    },
    payload,
  );

const impression = (payload: any) =>
  assertCustom(
    "impression",
    {
      ...genericSchema,
      headline: optional(string()),
      type: string(),
    },
    payload,
  );

const download = (payload: any) =>
  assertCustom(
    "download",
    {
      ...genericNavigationSchema,
      filename: string(),
    },
    payload,
  );

const searchResult = (payload: any) =>
  assertCustom(
    "searchResult",
    {
      ...genericNavigationSchema,
      value: string(),
      position: number(),
      page: optional(number()),
      level: searchLevel,
      type: optional(union([literal("autosuggest"), literal("most searched")])),
    },
    payload,
  );

const loginType = (payload: any) =>
  assertCustom(
    "loginType",
    {
      loginType: string(),
    },
    payload,
  );

const customEvent = (payload: any) => {
  const customEventType = type({
    eventName: string(),
    primaryCategory: string(),
  });
  try {
    assert(payload, customEventType);
  } catch (error: any) {
    throw new Error(
      `assertion error: invalid customEvent payload: ${error.message}`,
    );
  }
};

export default {
  pageLoaded,
  searchLoaded,
  accordion,
  genericNavigation,
  customNavigation,
  genericClick,
  teaserImpression,
  teaserNavigation,
  impression,
  download,
  searchResult,
  loginType,
  customEvent,
};
